import React, { useMemo } from 'react'
import { SplitFactoryProvider } from '@splitsoftware/splitio-react'
import { Treatments } from '@splitsoftware/splitio-react/types/splitio/splitio'

import settings, { APP_ENV } from 'shared/common/settings'

const browserKey = settings.getValue('SPLIT_IO_AUTHORIZATION_KEY')
const appEnv = settings.appEnv || 'development'

const browserAuthorization = new Map<APP_ENV, string>([
  ['production', browserKey],
  ['staging', browserKey],
  ['development', 'localhost'],
  ['preview', browserKey],
  ['local', 'localhost'],
])

const browserFeatures: Record<APP_ENV, Treatments> = {
  production: {},
  staging: {},
  preview: {},
  // eslint-disable-next-line global-require
  development: require('./splitio.local.json'),
  // eslint-disable-next-line global-require
  local: require('./splitio.local.json'),
}

interface Props {
  defaultKey?: string
  children: React.ReactElement
}

export const FeatureFlagProvider = ({
  defaultKey = 'BASIC',
  children,
}: Props) => {
  // SplitFactoryProvider recreates the SDK factory whenever `config` changes
  // identity, turning every flag off until it's ready again. Keep it stable
  // across parent re-renders (e.g. a server action revalidating the layout).
  const config = useMemo(
    () => ({
      core: {
        authorizationKey: browserAuthorization.get(appEnv) || 'localhost',
        key: defaultKey,
      },
      features: browserFeatures[appEnv],
    }),
    [defaultKey],
  )

  return (
    <SplitFactoryProvider config={config}>
      {({ client }) => {
        /**
         * Every use of useSplitTreatments or useSplitClient creates a new event listener
         * and is expected. The default max listener is 10, which creates a warning that
         * I'm disabling for feature flags.
         *
         * See https://github.com/splitio/react-client/issues/191
         */
        client?.setMaxListeners(20)

        return children
      }}
    </SplitFactoryProvider>
  )
}
